import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { isIOS, isMacOs, isSafari } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import ApplePayLogo from '~/assets/logos/apple-pay.png';
import GooglePayLogo from '~/assets/logos/google-pay.png';
import MobileMoneyLogo from '~/assets/logos/mobile-money.png';
import { BankIcon } from '~/components/icons/BankIcon';
import { CardIcon } from '~/components/icons/CardIcon';
import { GimacIcon } from '~/components/icons/GimacIcon';
import { Text } from '~/components/Typo/Typo';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { FontWeight } from '~/theme/constants';
import { getAvailablePaymentMethodOptions, PAYMENT_METHOD_ICON_KEYS } from '~/utils/paymentMethodsConfig';

import styles from './PaymentMethodSelection.module.scss';

const ICON_MAP: Record<(typeof PAYMENT_METHOD_ICON_KEYS)[keyof typeof PAYMENT_METHOD_ICON_KEYS], string> = {
  CARD: '', // Rendered by CardIcon component
  google: GooglePayLogo,
  apple: ApplePayLogo,
  gimac: '', // Rendered by GimacIcon component
  mobile: MobileMoneyLogo,
  EUMM: MobileMoneyLogo,
  BANK: '', // Rendered by BankIcon component
};

interface PaymentMethodSelectionProps {
  paymentInfos: GetPaymentResponse;
  onSelect: (specificType: string) => void;
}

export const PaymentMethodSelection: React.FC<PaymentMethodSelectionProps> = ({ paymentInfos, onSelect }) => {
  const { t } = useTranslation();
  const [focusedCard, setFocusedCard] = useState<string | null>(null);
  const isAppleDevice = isIOS || (isMacOs && isSafari);

  const availableMethods = React.useMemo(
    () => getAvailablePaymentMethodOptions(paymentInfos?.data?.meansOfPayment ?? [], isAppleDevice),
    [paymentInfos?.data?.meansOfPayment, isAppleDevice],
  );
  // console.log('availableMethods', availableMethods);

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapper_content}>
        <Text weight={FontWeight.SEMIBOLD} className={styles.title}>
          {t('checkoutPage:selectYourPaymentMethod')}
        </Text>
        <div className={styles.grid}>
          {availableMethods.map(method => {
            const isHighlighted = focusedCard === method.specificType;
            return (
              <button
                key={method.specificType}
                type="button"
                className={`${styles.card} ${isHighlighted ? styles.cardHovered : ''}`}
                onClick={() => onSelect(method.specificType)}
                onMouseEnter={() => setFocusedCard(method.specificType)}
                onMouseLeave={() => setFocusedCard(null)}
                onFocus={() => setFocusedCard(method.specificType)}
                onBlur={() => setFocusedCard(null)}
              >
                {isHighlighted && (
                  <span className={styles.checkmark} aria-hidden>
                    <Check size={16} strokeWidth={3} />
                  </span>
                )}
                <div className={styles.iconSlot}>
                  {method.iconKey === 'CARD' ? (
                    <CardIcon className={styles.icon} height={40} />
                  ) : method.iconKey === 'gimac' ? (
                    <GimacIcon className={styles.icon} height={40} />
                  ) : method.iconKey === 'BANK' ? (
                    <BankIcon className={styles.icon} height={40} />
                  ) : (
                    <img src={ICON_MAP[method.iconKey]} alt="" className={styles.icon} height={40} />
                  )}
                </div>
                <div className={styles.labelFrame}>
                  <span className={styles.label}>{t(method.titleKey)}</span>
                  {method.subtitleKey && <span className={styles.subtitle}>{t(method.subtitleKey)}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
